import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import { http, track } from "@/lib/api";
import { ProgramCard } from "@/components/ui-hot";

export const QUESTIONS = [
  { key: "level", q: "What is your current level?", opts: [["beginner", "Complete Beginner"], ["some", "Some Market Knowledge"], ["existing", "Existing Trader"], ["advanced", "Advanced Trader"]] },
  { key: "interest", q: "What do you want to learn?", opts: [["stock", "Stock Market"], ["fno", "F&O"], ["forex", "Forex"], ["commodities", "Commodities"], ["crypto", "Crypto"], ["complete", "Complete Market Education"], ["career", "Finance Career"]] },
  { key: "mode", q: "How do you prefer learning?", opts: [["offline", "Offline (Mohali)"], ["online", "Online"], ["hybrid", "Hybrid"]] },
  { key: "goal", q: "What is your goal?", opts: [["understand", "Understand markets"], ["trade", "Learn trading"], ["process", "Build a structured trading process"], ["nism", "Prepare for NISM"], ["career", "Explore finance career"], ["improve", "Improve existing knowledge"]] },
];

const RESTRICTED = new Set(["forex", "crypto"]);

export const CourseFinder = ({ primary }) => {
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const questions = primary ? QUESTIONS.map((q) => (q.key === "interest" ? { ...q, opts: q.opts.filter(([v]) => !RESTRICTED.has(v)) } : q)) : QUESTIONS;
  const pick = async (key, val) => {
    const next = { ...ans, [key]: val };
    setAns(next);
    if (step < questions.length - 1) return setStep(step + 1);
    setLoading(true);
    try { const { data } = await http.post("/recommend", next); setResult(data); track("course_finder_complete", next); } finally { setLoading(false); }
  };
  const reset = () => { setStep(0); setAns({}); setResult(null); };
  const q = questions[step];
  return (
    <div data-testid="course-finder" className="card p-6 sm:p-8 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      {!result ? (
        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <span className="font-mono text-xs text-slate-400">Question {step + 1} of {questions.length}</span>
            <div className="flex gap-1">{questions.map((_, i) => <span key={i} className={`h-1 w-8 rounded-full ${i <= step ? "bg-[var(--cyan)]" : "bg-white/10"}`} />)}</div>
          </div>
          <h3 data-testid="finder-question" className="text-2xl font-semibold text-slate-50 mb-6">{q.q}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {q.opts.map(([v, l]) => (
              <button key={v} data-testid={`finder-option-${q.key}-${v}`} disabled={loading} onClick={() => pick(q.key, v)} className="text-left rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-slate-200 transition-[border-color,background-color,transform] hover:border-[var(--blue)] hover:bg-white/[0.06] hover:-translate-y-0.5">{l}</button>
            ))}
          </div>
          {step > 0 && <button data-testid="finder-back" onClick={() => setStep(step - 1)} className="mt-5 text-xs text-slate-400 hover:text-slate-200">← Back</button>}
          {loading && <p className="mt-4 font-mono text-xs text-[var(--blue)]">Matching programs…</p>}
        </div>
      ) : (
        <div className="relative" data-testid="finder-result">
          <p className="eyebrow mb-2">Your recommended path</p>
          <h3 className="text-2xl font-semibold text-slate-50 mb-2">Based on your answers, these programs may be relevant.</h3>
          <p className="text-xs text-slate-500 mb-6">{result.note}</p>
          <div className="grid md:grid-cols-2 gap-5">{result.programs.map((c) => <ProgramCard key={c.slug} c={c} compact />)}</div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/book-counselling" data-testid="finder-book-counselling" className="btn btn-primary">Book a Free Counselling Session <ArrowRight size={16} /></Link>
            <button onClick={reset} data-testid="finder-restart" className="btn btn-outline"><RotateCcw size={14} /> Start over</button>
          </div>
        </div>
      )}
    </div>
  );
};
